var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
typeof window !== 'undefined' &&
    !window.__DS_WEB_VERSION__ &&
    (window.__DS_WEB_VERSION__ = '2.4.0');
import { generateHash } from '../../helpers';
import Link from '../Link';
import { AutocompleteListItem } from './AutocompleteListItem';
import * as Styles from './styles';
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}
/**
 * Componente Autocomplete do Design System OLX
 */
export const Autocomplete = (props) => {
    const { placeholder, onChange, onTextChange, optionsList = [], debounceTime, id, initialValue, overlayMaxHeight, actionElement, label, children, showDropdownAsDefault, className, clearInput, shadow = true } = props, restProps = __rest(props, ["placeholder", "onChange", "onTextChange", "optionsList", "debounceTime", "id", "initialValue", "overlayMaxHeight", "actionElement", "label", "children", "showDropdownAsDefault", "className", "clearInput", "shadow"]);
    const elementId = id || generateHash();
    const [searchTerm, setSearchTerm] = useState(initialValue || '');
    const [focusedItem, setFocusedItem] = useState(-1);
    const [showItems, setShowItems] = useState(false);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTime || 300);
    const childrenArraySize = Array.isArray(children) ? children.length : 1;
    useEffect(() => {
        clearInput && setSearchTerm('');
    }, [clearInput]);
    const handleClickOutside = (event) => {
        const { current: wrap } = wrapperRef;
        if (event.target === inputRef.current && searchTerm) {
            setShowItems(true);
            return;
        }
        if (wrap && !wrap.contains(event.target)) {
            setShowItems(false);
            setFocusedItem(-1);
        }
    };
    const handleKeyNavigation = (direction) => {
        var _a, _b, _c;
        const elementsToNavigate = ((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('ul button:not([disabled]), ul a[href], ul area[href], ul input:not([disabled]), ul select:not([disabled]), ul textarea:not([disabled]), ul [tabindex="0"]')) || null;
        if (!elementsToNavigate)
            return;
        let nextFocusedItem = focusedItem;
        if ((direction === 'ArrowUp' && focusedItem === 0) ||
            (direction === 'ArrowDown' && focusedItem === elementsToNavigate.length - 1)) {
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            setFocusedItem(-1);
            return;
        }
        if (direction === 'ArrowUp' && focusedItem < 0) {
            (_c = elementsToNavigate[elementsToNavigate.length - 1]) === null || _c === void 0 ? void 0 : _c.focus();
            setFocusedItem(elementsToNavigate.length - 1);
            return;
        }
        if (direction === 'ArrowUp') {
            if (focusedItem > optionsList.length - 1 || focusedItem > childrenArraySize - 1) {
                nextFocusedItem = 0;
            }
            nextFocusedItem = focusedItem - 1;
        }
        if (direction === 'ArrowDown') {
            if (focusedItem < 0) {
                nextFocusedItem = (optionsList.length > 0 ? optionsList.length : childrenArraySize) + 1;
            }
            nextFocusedItem = focusedItem + 1;
        }
        setFocusedItem(nextFocusedItem);
        elementsToNavigate[nextFocusedItem].focus();
    };
    const handleKeyDown = (event) => {
        var _a;
        const { key, target } = event;
        if (!((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))) {
            return;
        }
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            event.preventDefault();
            handleKeyNavigation(key);
        }
        if (key === 'Tab') {
            setShowItems(false);
            setFocusedItem(-1);
            return;
        }
    };
    useEffect(() => {
        if (typeof debouncedSearchTerm === 'string' && onChange) {
            onChange(debouncedSearchTerm);
        }
        if (typeof debouncedSearchTerm === 'string' && onTextChange) {
            onTextChange(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm]);
    useEffect(() => {
        window.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('keydown', handleClickOutside);
        window.addEventListener('keyup', handleClickOutside);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleClickOutside);
            window.removeEventListener('keyup', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
        };
    });
    const hasLabel = typeof label !== 'undefined' && label.trim().length > 0;
    const isLoading = restProps.state === 'loading';
    const showDropdownList = (showItems && !(restProps === null || restProps === void 0 ? void 0 : restProps.disabled) && !isLoading && (restProps === null || restProps === void 0 ? void 0 : restProps.state) !== 'error') || showDropdownAsDefault;
    return (React.createElement(Styles.AutocompleteWrapper, { "data-ds-component": "DS-Autocomplete", ref: wrapperRef, className: className },
        React.createElement("div", { className: "combobox-wrapper" },
            React.createElement("div", { role: "combobox", "aria-controls": "autocomplete-list", "aria-expanded": showItems && optionsList.length > 0, "aria-owns": "autocomplete-list", "aria-haspopup": "listbox", "aria-busy": restProps.state === 'loading', id: `${elementId}-combobox`, "data-testid": `${elementId}-combobox`, className: `${actionElement ? 'has-action-element' : ''}` },
                React.createElement(Styles.Input, Object.assign({ id: `${elementId}-input`, "data-testid": `${elementId}-input`, label: label || '', "aria-autocomplete": "list", "aria-label": "Autocomplete", placeholder: placeholder, onChange: (event) => {
                        setSearchTerm(event.target.value);
                        setShowItems(true);
                    }, ref: inputRef, autoComplete: "off", onFocus: () => setShowItems(true), value: searchTerm }, restProps, { className: `${actionElement ? 'has-action-element' : ''}` })),
                actionElement && !isLoading && React.createElement(Styles.ActionElementWrapper, null, actionElement)),
            showDropdownList && optionsList.length > 0 && (React.createElement(Styles.OptionsList, { "aria-labelledby": "ex1-label", "aria-live": "assertive", id: `${elementId}-autocomplete-list`, "data-testid": `${elementId}-autocomplete-list`, className: "listbox", hasLabel: hasLabel, style: { maxHeight: overlayMaxHeight || '500px' }, shadown: shadow }, optionsList.map((item, index) => {
                const { label, href, iconLeft, iconRight, id, margin, marginRight, marginBottom, marginTop } = item;
                return (React.createElement(AutocompleteListItem, { key: `${id || `${label}-${href}-${index}`}`, id: `${id || `result-item-${index}`}`, className: "result" },
                    React.createElement(Link, { href: href || '#', iconLeft: iconLeft, iconRight: iconRight, margin: margin, marginTop: marginTop, marginRight: marginRight, marginBottom: marginBottom }, label)));
            }))),
            showDropdownList && optionsList.length <= 0 && children && (React.createElement(Styles.OptionsList, { "aria-labelledby": "ex1-label", "aria-live": "assertive", id: `${elementId}-autocomplete-list`, "data-testid": `${elementId}-autocomplete-list`, className: "listbox", hasLabel: hasLabel, style: { maxHeight: overlayMaxHeight || '500px' }, shadown: shadow }, children)))));
};
Autocomplete.displayName = 'Autocomplete';
